import React, { useState } from 'react';
import toast from 'react-hot-toast';
import superAdminApi from '../../utils/superAdminApi';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../context/SuperAdminAuthContext';
import { BACKEND_URL } from '../../config';
import InstallAppButton from './InstallAppButton';
import AssistantChat from './AssistantChat';
import {
  FiGrid, FiHome, FiSliders, FiPackage, FiFileText, FiUsers, FiActivity,
  FiBarChart2, FiScissors, FiLogOut, FiUser, FiMenu, FiX, FiShield, FiDownload, FiDollarSign, FiSmartphone,
} from 'react-icons/fi';

const NavItem = ({ to, icon: Icon, label, onClick }) => {
  const location = useLocation();
  const isActive = to ? location.pathname === to || location.pathname.startsWith(to + '/') : false;
  if (onClick) {
    return (
      <button className="nav-item" onClick={onClick}>
        <Icon size={16} />{label}
      </button>
    );
  }
  return (
    <NavLink to={to} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`} end={to === '/superadmin'}>
      <Icon size={16} />{label}
    </NavLink>
  );
};

export default function SuperAdminLayout() {
  const { superAdmin, logout, updateProfile } = useSuperAdminAuth();
  const [account, setAccount] = useState(null); // My Account form
  const [savingAccount, setSavingAccount] = useState(false);
  const openAccount = () => setAccount({ name: superAdmin?.name || '', email: superAdmin?.email || '', currentPassword: '', newPassword: '' });
  const saveAccount = async () => {
    setSavingAccount(true);
    try {
      const r = await superAdminApi.put('/auth/me', account);
      updateProfile(r.data.superAdmin);
      toast.success('Account updated');
      setAccount(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
    setSavingAccount(false);
  };
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const titles = {
    '/superadmin': 'Platform Dashboard',
    '/superadmin/shops': 'Shops',
    '/superadmin/config': 'Cloth & Style Management',
    '/superadmin/inventory': 'Quick Sale Items (Inventory)',
    '/superadmin/fabric': 'Cloth Stock & Cutting',
    '/superadmin/sales': 'Cross-Shop Sales',
    '/superadmin/payments': 'Payments Received',
    '/superadmin/customers': 'Customers',
    '/superadmin/activity': 'Activity & Statement History',
    '/superadmin/staff': 'Staff & Login Tracking',
    '/superadmin/reports': 'Platform Reports',
    '/superadmin/app-version': 'App Version & Updates',
  };

  const title = Object.entries(titles).reverse().find(([p]) => location.pathname.startsWith(p))?.[1] || 'Super Admin';

  const handleLogout = () => { logout(); navigate('/superadmin/login'); };

  return (
    <div className="app-layout">
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99 }} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiShield size={20} color="#f59e0b" />
            <div>
              <div className="sidebar-logo">Super Admin</div>
              <div className="sidebar-sub">Platform Owner Panel</div>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <NavItem to="/superadmin" icon={FiGrid} label="Dashboard" />

          <div className="nav-group-label">Shops</div>
          <NavItem to="/superadmin/shops" icon={FiHome} label="All Shops" />

          <div className="nav-group-label">Master Data</div>
          <NavItem to="/superadmin/config" icon={FiSliders} label="Cloth & Styles" />

          <div className="nav-group-label">Cross-Shop</div>
          <NavItem to="/superadmin/inventory" icon={FiPackage} label="Quick Sale Items" />
          <NavItem to="/superadmin/fabric" icon={FiScissors} label="Cloth & Cutting" />
          <NavItem to="/superadmin/sales" icon={FiFileText} label="Sales" />
          <NavItem to="/superadmin/payments" icon={FiDollarSign} label="Payments" />
          <NavItem to="/superadmin/customers" icon={FiUsers} label="Customers" />
          <NavItem to="/superadmin/activity" icon={FiActivity} label="Activity History" />
          <NavItem to="/superadmin/staff" icon={FiShield} label="Staff & Logins" />
          <NavItem to="/superadmin/reports" icon={FiBarChart2} label="Reports" />
          <NavItem to="/superadmin/app-version" icon={FiSmartphone} label="App Version" />
        </nav>

        <div className="sidebar-footer">
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8, paddingLeft: 8 }}>
            👤 {superAdmin?.name} (Super Admin)
            <div style={{ fontSize: 11 }}>{superAdmin?.email}</div>
          </div>
          <NavItem icon={FiUser} label="My Account" onClick={openAccount} />
          <NavItem icon={FiLogOut} label="Logout" onClick={handleLogout} />
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-outline btn-icon sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <FiX size={18} /> : <FiMenu size={18} />}
            </button>
            <h1 className="topbar-title">{title}</h1>
          </div>
          <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <InstallAppButton />
            <a
              href={`${BACKEND_URL}/download/app-latest.apk`}
              className="btn btn-outline btn-sm sa-hide-mobile"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              title="Always downloads the newest mobile app build"
            >
              <FiDownload size={14} /> Download Latest App
            </a>
            <span className="sa-hide-mobile" style={{ fontSize: 12, color: '#64748b' }}>
              {new Date().toLocaleDateString('en-AE', { timeZone: 'Asia/Riyadh', weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
          </div>
        </header>
        <main className="page-content">
          <Outlet />
        </main>
      </div>

      <AssistantChat api={superAdminApi} />

      {account && (
        <div className="modal-overlay" onClick={() => setAccount(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">👤 My Account</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setAccount(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-control" value={account.name} onChange={e => setAccount({ ...account, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Login email</label>
                <input type="email" className="form-control" value={account.email} onChange={e => setAccount({ ...account, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">New password (optional)</label>
                <input type="password" className="form-control" placeholder="Leave blank to keep the same" value={account.newPassword}
                  onChange={e => setAccount({ ...account, newPassword: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Current password</label>
                <input type="password" className="form-control" placeholder="Needed to change email or password" value={account.currentPassword}
                  onChange={e => setAccount({ ...account, currentPassword: e.target.value })} />
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                To change any shop staff's email: All Shops → shop → Shop Users → ✏️, or Staff & Logins.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setAccount(null)}>Cancel</button>
              <button className="btn btn-accent" onClick={saveAccount} disabled={savingAccount}>{savingAccount ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
